import React from "react";
import styled from "styled-components";
import { FiBriefcase, FiCalendar, FiExternalLink, FiFileText } from "react-icons/fi";

const Card = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.card || "#171721"};
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 28px 26px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.25);
  transition: all 0.35s ease;
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: rgba(133, 76, 230, 0.55);
    transform: translateY(-4px);
    box-shadow: 0 12px 36px rgba(133, 76, 230, 0.18);
  }

  @media (max-width: 640px) {
    padding: 20px 16px;
    gap: 14px;
  }
`;

const CardTop = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: 100%;
`;

const CompanyLogoWrapper = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  @media (max-width: 640px) {
    width: 44px;
    height: 44px;
  }
`;

const HeaderDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  flex-grow: 1;
`;

const RoleTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  letter-spacing: -0.01em;

  @media (max-width: 640px) {
    font-size: 17px;
  }
`;

const CompanyName = styled.div`
  font-size: 14.5px;
  font-weight: 600;
  color: #c084fc;
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 640px) {
    font-size: 13px;
  }
`;

const DateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;

  svg {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.45);
  }
`;

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: ${(props) => (props.isPresent ? "#22c55e" : "rgba(255, 255, 255, 0.4)")};
  box-shadow: ${(props) => (props.isPresent ? "0 0 8px #22c55e" : "none")};
  display: inline-block;
`;

const Description = styled.p`
  font-size: 14.5px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.72);
  margin: 0;

  @media (max-width: 640px) {
    font-size: 13.5px;
  }
`;

const SkillsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 2px;
`;

const SkillsLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.45);
`;

const SkillsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SkillBadge = styled.span`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 5px 11px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.8);
  letter-spacing: 0.02em;
  transition: all 0.2s ease;

  ${Card}:hover & {
    border-color: rgba(133, 76, 230, 0.3);
  }

  &:hover {
    background: rgba(133, 76, 230, 0.15) !important;
    border-color: rgba(133, 76, 230, 0.6) !important;
    color: #ffffff !important;
  }
`;

const DocButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  background: rgba(133, 76, 230, 0.12);
  border: 1px solid rgba(133, 76, 230, 0.4);
  color: #e879f9;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 10px;
  text-decoration: none;
  margin-top: 4px;
  transition: all 0.25s ease;

  &:hover {
    background: linear-gradient(135deg, #854CE6 0%, #a855f7 100%);
    color: #ffffff;
    border-color: #854CE6;
    box-shadow: 0 4px 18px rgba(133, 76, 230, 0.4);
    transform: translateY(-2px);
  }

  svg {
    font-size: 14px;
  }
`;

const ExperienceCard = ({ experience }) => {
  const isPresent = experience.date?.toLowerCase().includes("present");

  return (
    <Card className="spotlight-card">
      <CardTop>
        <CompanyLogoWrapper>
          <img
            src={experience.img}
            alt={experience.company}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
        </CompanyLogoWrapper>

        <HeaderDetails>
          <RoleTitle>{experience.role}</RoleTitle>
          <CompanyName>
            <FiBriefcase /> {experience.company}
          </CompanyName>
          <DateBadge>
            <StatusDot isPresent={isPresent} />
            <FiCalendar /> {experience.date}
          </DateBadge>
        </HeaderDetails>
      </CardTop>

      {experience.desc && <Description>{experience.desc}</Description>}

      {experience.skills && experience.skills.length > 0 && (
        <SkillsSection>
          <SkillsLabel>Technologies & Skills:</SkillsLabel>
          <SkillsWrapper>
            {experience.skills.map((skill, index) => (
              <SkillBadge key={index}>{skill}</SkillBadge>
            ))}
          </SkillsWrapper>
        </SkillsSection>
      )}

      {experience.doc && (
        <DocButton
          href={experience.doc}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FiFileText /> View Verified Certificate / Doc <FiExternalLink />
        </DocButton>
      )}
    </Card>
  );
};

export default ExperienceCard;